import type { LucideIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import { formatCount, formatTaka } from '@/utils/number';
import { PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';
import type { PcmpSkippedProposal } from '../../types/pcmpQueue.types';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';
import { PcmpOutcomeDialog } from './PcmpOutcomeDialog';

const textFormSchema = (required: string) =>
  z.object({ text: z.string().trim().min(1, required).max(PCMP_MKT_COMMENT_MAX) });

type TextFormValues = z.infer<ReturnType<typeof textFormSchema>>;

export interface PcmpBulkOutcome {
  title: string;
  summary: string;
  skipped: PcmpSkippedProposal[];
}

interface PcmpBulkCommentDialogProps {
  title: string;
  description: string;
  icon: LucideIcon;
  submitLabel: string;
  pendingLabel: string;
  submitVariant?: 'default' | 'destructive';
  /** What the selection covers, e.g. "Selected proposals". */
  scope: string;
  count: number;
  amount: number;
  /** What happens to them, and that changed ones are left out. */
  note: string;
  field: { label: 'Comment' | 'Reason'; placeholder: string };
  isPending: boolean;
  error: unknown;
  onSubmit: (text: string) => void;
  /** Set once the API left proposals out: the dialog then lists them. */
  outcome: PcmpBulkOutcome | null;
  refNoOf?: ((proposalId: number) => string | undefined) | undefined;
  onClose: () => void;
}

/**
 * The frame of a bulk batch action: what the selection covers, a required
 * comment or reason, the API error inline, then the proposals it left out.
 */
export function PcmpBulkCommentDialog({
  title,
  description,
  icon,
  submitLabel,
  pendingLabel,
  submitVariant = 'default',
  scope,
  count,
  amount,
  note,
  field,
  isPending,
  error,
  onSubmit,
  outcome,
  refNoOf,
  onClose,
}: PcmpBulkCommentDialogProps) {
  const form = useForm<TextFormValues>({
    resolver: zodResolver(textFormSchema(`A ${field.label.toLowerCase()} is required.`)),
    defaultValues: { text: '' },
  });

  if (outcome) {
    return (
      <PcmpOutcomeDialog
        title={outcome.title}
        summary={outcome.summary}
        skipped={outcome.skipped}
        refNoOf={refNoOf}
        onClose={onClose}
      />
    );
  }

  const submit = form.handleSubmit((values) => onSubmit(values.text));

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={title}
        description={description}
        icon={icon}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void submit(event)}
        submitLabel={submitLabel}
        submitVariant={submitVariant}
        pendingLabel={pendingLabel}
      >
        <div className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          <span className="text-xs text-muted-foreground">{scope}</span>
          <span className="font-medium">
            {formatCount(count, 'proposal')} · {formatTaka(amount)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{note}</p>
        <PcmpCommentField
          control={form.control}
          name="text"
          label={field.label}
          required
          placeholder={field.placeholder}
          description="Recorded on each proposal's timeline."
        />
      </PcmpEditDialog>
    </Form>
  );
}
